/**
 * Background job listing and inspection (Celery-style job records for streams/import, etc.).
 *
 * Where: `StreamJobsList`, `UserSyncJobsList`, `StreamJobDetailsModal`.
 * Uses: `apiClient` against `/jobs/` routes.
 * Contract: GET `/streams/:streamUuid/jobs/` → `response.data` as `JobListResponse`; POST `/jobs/create/`; PATCH `/jobs/:jobUuid/update/`; POST `/jobs/:jobUuid/retry-failed-urls/`; POST multipart `/extract-urls/` → `response.data` as `ExtractUrlsResponse`.
 * Behavior: stream job list supports `page_token`, `include_sub_jobs`, `status` filter.
 */

import { apiClient } from './client';

export interface Job {
  key: string;
  queue: string;
  status: string;
  progress?: number;
  failed_reason?: string;
  available_at?: string;
  started_at?: string;
  payload?: {
    urls?: string[];
    url?: string;
    processed_urls?: Record<string, string>;
    parent_job_uuid?: string;
    from?: string;
    to?: string;
  };
  retries?: number;
  created_at: string;
}

export interface JobListResponse {
  output: {
    items: Job[];
    next_page_token?: string;
    previous_page_token?: string;
    total_results: number;
  };
}

export interface ExtractUrlsResponse {
  output: {
    urls: string[];
    count: number;
    max_urls: number;
    max_file_size_bytes: number;
  };
}

export const jobApi = {
  /**
   * List jobs for a stream
   */
  getStreamJobs: async (streamUuid: string, params?: {
    status?: string;
    page_token?: string;
    max_results?: number;
    include_sub_jobs?: boolean;
  }): Promise<JobListResponse> => {
    const response = await apiClient.get<JobListResponse>(
      `/streams/${streamUuid}/jobs/`,
      { params }
    );
    return response.data;
  },

  /**
   * Create a job
   */
  createJob: async (data: {
    stream_uuid: string;
    pipeline_uuid: string;
    urls: string[];
    available_at?: string;
  }): Promise<{ output: Job }> => {
    const response = await apiClient.post<{ output: Job }>('/jobs/create/', data);
    return response.data;
  },

  /**
   * Update a job
   */
  updateJob: async (jobUuid: string, data: {
    status?: string;
    available_at?: string;
  }): Promise<{ output: Job }> => {
    const response = await apiClient.patch<{ output: Job }>(
      `/jobs/${jobUuid}/update/`,
      data
    );
    return response.data;
  },

  /**
   * Retry failed URLs in a job
   */
  retryFailedUrls: async (jobUuid: string): Promise<{ output: Job }> => {
    const response = await apiClient.post<{ output: Job }>(
      `/jobs/${jobUuid}/retry-failed-urls/`
    );
    return response.data;
  },

  /**
   * Extract LinkedIn URLs from text or CSV file
   */
  extractUrls: async (data: {
    text?: string;
    file?: File;
  }): Promise<ExtractUrlsResponse> => {
    const formData = new FormData();
    if (data.text) {
      formData.append('text', data.text);
    }
    if (data.file) {
      formData.append('file', data.file);
    }
    
    const response = await apiClient.post<ExtractUrlsResponse>(
      '/extract-urls/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
};


